// A commissioner fixing a team's lineup for a week that is over
// (COMMISSIONER_OVERRIDES.md, J1). Every slot of that week with a choice of the
// players the team had that week, a reason, and what will happen said before
// it is saved: the week is re-scored and both managers are told.

import { useMemo, useState } from 'react';
import type { LineupRowView, LineupView } from '@fsp/domain';
import { Sheet } from '../../kit/overlays';
import { tell, Button, Callout, Choice, Stack, Text, TextArea } from '../../kit/ui';
import { fixLineup } from '../../data/overrides';

const cell = (r: { slot: string; slotIndex: number }) => `${r.slot}|${r.slotIndex}`;

export function FixLineupSheet({
  view,
  leagueId,
  opened,
  onClose,
}: {
  view: LineupView;
  leagueId: string;
  opened: boolean;
  onClose: () => void;
}) {
  const rows: LineupRowView[] = useMemo(
    () => [...view.starters, ...view.bench, ...view.reserve],
    [view],
  );
  const players = useMemo(
    () =>
      rows
        .filter((r) => r.player !== null)
        .map((r) => ({
          value: r.player!.id,
          // HIS POINTS THAT WEEK, which is what a fix is about.
          label: `${r.player!.name} · ${r.player!.positions[0] ?? ''}${r.points === null ? '' : ` · ${r.points.toFixed(1)}`}`,
        })),
    [rows],
  );
  const [picked, setPicked] = useState<Record<string, string | null>>({});
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const now = (r: LineupRowView) =>
    cell(r) in picked ? (picked[cell(r)] ?? null) : (r.player?.id ?? null);
  const changed = rows.filter((r) => now(r) !== (r.player?.id ?? null));
  const ready = changed.length > 0 && reason.trim() !== '' && !saving;

  const close = () => {
    setPicked({});
    setReason('');
    setProblem(null);
    onClose();
  };

  const save = async () => {
    setSaving(true);
    setProblem(null);
    const said = await fixLineup(leagueId, view.teamId, view.period.index, {
      slots: changed.map((r) => ({ slot: r.slot, slotIndex: r.slotIndex, playerId: now(r) })),
      reason: reason.trim(),
    });
    setSaving(false);
    if (!said.ok) return setProblem(said.message);
    close();
    // WHAT COULD NOT BE BROUGHT UP TO DATE after the scores moved, said: the
    // change stands and the managers were told (the review of J1, 1.5).
    if (said.problems.length > 0) tell(said.problems.join(' '), 'warn');
  };

  return (
    <Sheet
      opened={opened}
      onClose={close}
      title={`Fix ${view.team.name}’s lineup · ${view.period.label}`}
    >
      <Stack gap={3}>
        {rows.map((r) => (
          <Choice
            key={cell(r)}
            label={r.slotKind === 'starter' ? r.slot : `${r.slot} (${r.slotKind})`}
            options={players}
            value={now(r)}
            onChange={(v) => setPicked((p) => ({ ...p, [cell(r)]: v }))}
            placeholder="Empty"
            clearable
          />
        ))}
        <TextArea
          label="Why"
          placeholder="What went wrong, in a sentence the league will read"
          value={reason}
          onChange={setReason}
          maxLength={300}
        />
        <Callout>
          {view.period.label} will be scored again from this lineup. The managers of both teams will
          be told, and the league chat and the commissioner log will say so, with your reason.
        </Callout>
        {problem !== null ? <Callout tone="bad">{problem}</Callout> : null}
        <Button onClick={() => void save()} disabled={!ready} loading={saving}>
          Save fix
        </Button>
        {changed.length === 0 ? (
          <Text tone="secondary" size="xs">
            Change a slot to fix it.
          </Text>
        ) : null}
      </Stack>
    </Sheet>
  );
}
